import React, { useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import * as authService from '../../services/authService'
import { exchangePoints, getPointsPromotionalMessage, notifyUserSummaryUpdated } from '../../services/pointsService'
import type { PointsOutletContext } from './ModernPointsLayout'

/** แท็บ แลกเปลี่ยนคะแนนเป็นเงิน */
const PointsPointtomoneyPage: React.FC = () => {
  const {
    getAssetUrl,
    pointsText,
    pointsConfig,
    activePointsToCashRules,
    refreshUserSummary,
  } = useOutletContext<PointsOutletContext>()
  const logoUrl = getAssetUrl('/img/logo-m.png')
  const [submittingRuleId, setSubmittingRuleId] = useState<string | null>(null)

  const exchangeEnabled = pointsConfig?.system?.pointsToCashEnabled === true
  const systemStatusText = exchangeEnabled && activePointsToCashRules.length > 0
    ? 'สามารถแลกคะแนนเป็นเงินได้'
    : exchangeEnabled
      ? 'ยังไม่มีรายการแลกคะแนนเป็นเงินที่เปิดใช้งาน'
      : 'ปิดระบบแลกคะแนนเป็นเงิน'
  const systemStatusClass = exchangeEnabled && activePointsToCashRules.length > 0 ? 'c_success' : 'c_danger'
  const promotionalMessage = getPointsPromotionalMessage(pointsConfig)

  const packageList = useMemo(
    () => activePointsToCashRules,
    [activePointsToCashRules]
  )

  const handleExchange = async (ruleId: string) => {
    const currentUser = authService.getCurrentUser()
    const tenantId = currentUser?.tenantId
    const userId = currentUser?.id
    const selectedRule = packageList.find((rule) => rule.id === ruleId)

    if (!tenantId || !userId || !selectedRule) {
      await Swal.fire({ icon: 'error', title: 'ไม่พบข้อมูลผู้ใช้', confirmButtonColor: '#0d6efd' })
      return
    }

    const confirmed = await Swal.fire({
      icon: 'question',
      title: 'ยืนยันการแลกคะแนนเป็นเงิน',
      html: `ใช้ <strong>${Number(selectedRule.inputAmount).toLocaleString('th-TH')}</strong> คะแนน เพื่อรับเครดิต <strong>${Number(selectedRule.outputAmount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> บาท`,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#0d6efd',
    })

    if (!confirmed.isConfirmed) return

    try {
      setSubmittingRuleId(ruleId)
      const response = await exchangePoints({
        tenantId,
        userId,
        direction: 'points-to-cash',
        ruleId,
      })
      notifyUserSummaryUpdated()
      await refreshUserSummary()
      await Swal.fire({
        icon: 'success',
        title: response.message || 'แลกคะแนนเป็นเงินสำเร็จ',
        confirmButtonColor: '#0d6efd',
      })
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'แลกคะแนนเป็นเงินไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'เกิดข้อผิดพลาด',
        confirmButtonColor: '#0d6efd',
      })
    } finally {
      setSubmittingRuleId(null)
    }
  }

  return (
    <>
      <div className="c_quinary text-center">
        <h3 className="c_tertiary font_md">แลกเปลี่ยนคะแนนเป็นเงิน</h3>
      </div>

      <div className="light_sd border_10 p-3 p-md-4 bg_op_secondary bg_blur mt-3 mb-3">
        <table className="table bg_secondary table-bordered mb-4">
          <tbody>
            <tr>
              <th scope="row">ระบบการแลกเปลี่ยน (เปิด / ปิด)</th>
              <td className={systemStatusClass}>{systemStatusText}</td>
            </tr>
          </tbody>
        </table>

        <div className="c_tertiary text-center">
          <h5 className="">คะแนนของคุณ</h5>
        </div>
        <div className="p-2 mb-4">
          <h2 className="text-center mb-0">
            <span className="bg_secondary c_primary border_5 font_md px-2">{pointsText}</span>
          </h2>
        </div>

        <div className="row mx__1" style={{ rowGap: 8 }}>
          {packageList.map((rule) => (
            <div key={rule.id} className="col-6 col-lg-3 px-1 d-flex">
              <div className="d-flex flex-column w-100">
                <div className="bg_gd_primary bdc_primary border_5 p-2">
                  <figure className="mb-0 mx-auto" style={{ width: '80%' }}>
                    <img src={logoUrl} alt="" />
                  </figure>
                </div>
                <div className="my-2 text-center d-flex flex-column justify-content-center" style={{ minHeight: 56 }}>
                  <h5 className="c_tertiary text-center font_md mx-auto mb-1">
                    {Number(rule.inputAmount).toLocaleString('th-TH')} คะแนน
                  </h5>
                  <div className="c_tertiary text-center font_sm">
                    รับ {Number(rule.outputAmount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท
                  </div>
                </div>
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bg_gd_quaternary border_10 ins_sd w-100 border-0 mt-auto"
                  onClick={() => void handleExchange(rule.id)}
                  disabled={submittingRuleId === rule.id}
                >
                  <h5 className="c_tertiary d-flex align-items-center mb-0">
                    {submittingRuleId === rule.id ? 'กำลังแลก...' : `${Number(rule.inputAmount).toLocaleString('th-TH')} Pts.`}
                  </h5>
                </button>
              </div>
            </div>
          ))}
        </div>

        {promotionalMessage ? (
          <h5 className="c_tertiary mt-4">{promotionalMessage}</h5>
        ) : null}
      </div>
    </>
  )
}

export default PointsPointtomoneyPage
